import React, { useEffect, useState } from "react";
import { Customer } from "../models/customer_model";
import { Address } from "../models/address_model";
import { primaryColor } from "../services/theme_service";
import { getUserFromId, getAddressList, removeAddress } from "../services/api_service";
import AddAddress from "./add_address";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderDetailTrack, OrderItem, OrderResponse } from "../models/orderStatus_model";
  import packIcon from "../assets/svg/orders.svg";

// ========================== MAIN PROFILE PAGE ==========================
export const ProfilePage: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const section = queryParams.get("section");

  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [activeSection, setActiveSection] = useState("Profile");
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  const userId = localStorage.getItem("userId");

  // Detect mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    if (section) {
      setActiveSection(section)
    }
  }, [section])

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (userId) {
        const user = await getUserFromId(userId);
        if (user) { setCustomer(user);
        } else {
           localStorage.removeItem("userId");

        }
        setLoading(false);
      }
    };
    fetchProfile();
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    if (!userId) return;
    setLoadingAddresses(true);
    const list = await getAddressList(userId);
    setAddresses(list || []);
    setLoadingAddresses(false);
  };

  if (loading) return <LoadingScreen text="Fetching profile details..." />;
  if (!customer) return <LoadingScreen text="No profile found" emoji="😕" />;

  return isMobile ? (
    <ProfileMobile
      customer={customer}
      addresses={addresses}
      loadingAddresses={loadingAddresses}
      fetchAddresses={fetchAddresses}
      tab={activeSection}
    />
  ) : (
    <ProfileWeb
      customer={customer}
      addresses={addresses}
      loadingAddresses={loadingAddresses}
      fetchAddresses={fetchAddresses}
      activeSection={activeSection}
      setActiveSection={setActiveSection}
    />
  );
};

// ========================== WEB VIEW ==========================
const ProfileWeb: React.FC<{
  customer: Customer;
  addresses: Address[];
  loadingAddresses: boolean;
  fetchAddresses: () => void;
  activeSection: string;
  setActiveSection: (val: string) => void;
}> = ({ customer, addresses, loadingAddresses, fetchAddresses, activeSection, setActiveSection }) => {
  const navigate = useNavigate();

  return (
    <div style={webStyles.page}>

      <div style={webStyles.contentRow}>
        <Sidebar customer={customer} activeSection={activeSection} setActiveSection={setActiveSection} />
        <div style={webStyles.section}>
          {activeSection === "Profile" && <ProfileDetails customer={customer} />}
          {activeSection === "Orders" && <OrdersWeb userId={customer.user_id ?? "2"}></OrdersWeb>}
          {activeSection === "Wishlist" && <div>📦 Wishlist coming soon...</div>}
          {activeSection === "Saved Address" && <AddressList addresses={addresses} loading={loadingAddresses} fetchAddresses={fetchAddresses} />}
          {activeSection === "Logout" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                fontSize: 20,
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                textAlign: "center",
              }}
            >
              <span>Are you sure you want to logout?</span>
              <button
                onClick={() => {
                  localStorage.removeItem("userId");
                  navigate("/");
                }}
                style={{
                  padding: "10px 20px",
                  backgroundColor: primaryColor,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 15,
                  marginTop: "10px",
                  cursor: "pointer",
                  fontWeight: 600
                }}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ========================== MOBILE VIEW ==========================
const ProfileMobile: React.FC<{
  customer: Customer;
  addresses: Address[];
  loadingAddresses: boolean;
  fetchAddresses: () => void;
  tab: String;
}> = ({ customer, addresses, loadingAddresses, fetchAddresses, tab }) => {
  const [activeTab, setActiveTab] = useState(tab);
  const navigate = useNavigate();

  const tabs = [
    { label: "Profile", emoji: "👤", content: <ProfileDetails customer={customer} /> },
    { label: "Orders", emoji: "📦", content: <OrdersWeb userId={customer.user_id ?? "2"}></OrdersWeb>},
    { label: "Address", emoji: "🏠", content: <AddressList addresses={addresses} loading={loadingAddresses} fetchAddresses={fetchAddresses} /> },
    {
      label: "Logout", emoji: "🚪", content: (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center", marginTop: 20, fontSize: 18 }}>
          <span>Are you sure you want to logout?</span>
          <button
            onClick={() => {
              localStorage.removeItem("userId");
              navigate("/")
            }}
            style={{
              padding: "10px 20px",
              backgroundColor: primaryColor,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 15,
              marginTop: "7px",
              cursor: "pointer",
              fontWeight: 600
            }}
          >
            Logout
          </button>
        </div>
      )
    },
  ];

  return (
    <div style={mobileStyles.page}>

      <div style={mobileStyles.profileHeader}>
        <div style={mobileStyles.avatar}>{customer.user_name?.charAt(0).toUpperCase()}</div>
        <div>
          <p style={mobileStyles.name}>{customer.user_name}</p>
          <p style={mobileStyles.phone}>{customer.mobile_number}</p>
        </div>
      </div>

      {/* TABS */}
      <div style={mobileStyles.tabsContainer}>
        {tabs.map(tab => (
          <div
            key={tab.label}
            style={{
              ...mobileStyles.tab,
              backgroundColor: activeTab === tab.label ? "#ffe6ef" : "#fff",
              fontWeight: 500,
            }}
            onClick={() => setActiveTab(tab.label)}
          >
            {tab.emoji} {tab.label}
          </div>
        ))}
      </div>

      {/* CONTENT */}
      <div style={mobileStyles.tabContent}>
        {tabs.find(tab => tab.label === activeTab)?.content}
      </div>
    </div>
  );
};

// ========================== SIDEBAR ==========================
const Sidebar: React.FC<{ activeSection: string; setActiveSection: (val: string) => void; customer: Customer }> = ({ activeSection, setActiveSection, customer }) => (
  <div style={webStyles.sidebar}>
    <div style={webStyles.profileHeader}>
      <div style={webStyles.avatar}>{customer.user_name?.charAt(0).toUpperCase()}</div>
      <div>
        <p style={webStyles.name}>{customer.user_name}</p>
        <p style={webStyles.phone}>{customer.mobile_number}</p>
      </div>
    </div>
    {["Profile", "Orders", "Wishlist", "Saved Address", "Logout"].map(label => (
      <div
        key={label}
        style={{
          ...webStyles.menuItem,
          backgroundColor: activeSection === label ? "#ffe6ef" : "transparent",
        }}
        onClick={() => setActiveSection(label)}
      >
        {label === "Profile" ? "👤" : label === "Orders" ? "📦" : label === "Saved Address" ? "🏠" : label === "Wishlist" ? "❤️" : "🚪"} {label}
      </div>
    ))}
  </div>
);

// ========================== LOADING ==========================
const LoadingScreen: React.FC<{ text: string; emoji?: string }> = ({ text, emoji }) => (
  <div style={webStyles.page}>
    <div style={webStyles.statusContainer}>
      <div style={webStyles.loaderBox}>
        {!emoji && <div style={webStyles.spinner}></div>}
        {emoji && <span style={webStyles.statusEmoji}>{emoji}</span>}
        <span style={webStyles.statusText}>{text}</span>
      </div>
    </div>
  </div>
);

// ========================== PROFILE DETAILS ==========================
const ProfileDetails: React.FC<{ customer: Customer }> = ({ customer }) => {
  const [editedCustomer, setEditedCustomer] = useState<Customer>(customer);
  const [isEdited, setIsEdited] = useState(false);

  const handleInputChange = (field: keyof Customer, value: string) => {
    setEditedCustomer({ ...editedCustomer, [field]: value });
    setIsEdited(true);
  };

  const handleSave = () => {
    setIsEdited(false);
    console.log("Saved", editedCustomer);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <label>Name</label>
      <input value={editedCustomer.user_name || ""} onChange={(e) => handleInputChange("user_name", e.target.value)} style={inputStyle} />
      <label>Profile ID</label>
      <input value={editedCustomer.user_id?.toString() || ""} disabled style={{ ...inputStyle, backgroundColor: "#f5f5f5" }} />
      <label>Email</label>
      <input value={editedCustomer.email || ""} onChange={(e) => handleInputChange("email", e.target.value)} style={inputStyle} />
      <label>Phone Number</label>
      <input value={editedCustomer.mobile_number || ""} disabled style={{ ...inputStyle, backgroundColor: "#f5f5f5" }} />
      {isEdited && <button onClick={handleSave} style={saveBtnStyle}>Save</button>}
    </div>
  );
};

// ========================== ORDERS DETAILS ==========================
export const OrdersWeb: React.FC<{ userId: string | number }> = ({ userId }) => {
  const [orders, setOrders] = useState<OrderResponse>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showTracking, setShowTracking] = useState<Record<string, boolean>>({}); // track visibility per order

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount / 100);
  };

  const formatDate = (isoDate?: string) => {
    if (!isoDate) return "—";
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(false);
      const res = await fetch(
        `https://manthrisnonprod.azurewebsites.net/users/orders?Page=1&PageSize=10&UserId=${userId}`,
        { headers: { accept: "text/plain" } }
      );
      if (!res.ok) throw new Error("Failed to fetch orders");
      const data = await res.json();
      setOrders(data.records || []);
    } catch (err) {
      console.error(err);
      setError(true);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [userId]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300, flexDirection: "column", gap: 12 }}>
        <div style={{ border: `3px solid #fff`, borderTop: `3px solid ${primaryColor}`, borderRadius: "50%", width: 40, height: 40, animation: "spin 1s linear infinite" }} />
        <span style={{ fontWeight: 500 }}>Processing your orders...</span>
      </div>
    );
  }

  if (error || orders.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300, fontSize: 16 }}>
        No orders found
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {orders.map((order) => {
        const payment = order.order_payment_details?.[0];
        const firstItem = order.order_details?.[0];
        const isDelivered = order.status === "Delivered";

        return (
          <div key={order.order_id} style={{ backgroundColor: "#FFF7EB", maxWidth: 1000, borderRadius: 12, padding: 16, display: "flex", flexDirection: "column" }}>
            {/* Top Row: Icon + Status + Delivery Date */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img src={packIcon} alt="Pack" width={32} height={32} />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontWeight: 600, fontSize: 16 }}>{order.status ?? "—"}</span>
                {firstItem?.expected_delivery_date && (
                  <span style={{ fontSize: 14, color: "#555" }}>
                    {isDelivered ? `Delivered on: ${formatDate(firstItem.expected_delivery_date)}` : `Expected delivery: ${formatDate(firstItem.expected_delivery_date)}`}
                  </span>
                )}
              </div>
            </div>

            {/* Items List */}
            <div style={{ backgroundColor: "#fff", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
              {order.order_details?.map((item: OrderItem) => (
                <div key={item.order_detail_id} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <img
                      src={item.item_image}
                      alt={item.title}
                      style={{ width: 80, height: 90, borderRadius: 6, objectFit: "cover" }}
                    />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>{item.title}</p>
                      <div style={{ marginTop: 4, fontWeight: 500 }}>{formatCurrency(item.final_price)}</div>
                      <div style={{ marginTop: 6, fontSize: 14 }}>Qty: {item.quantity}</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                    <button
                      style={{
                        flex: 1,
                        padding: "6px 0",
                        border: `1px solid ${primaryColor}`,
                        borderRadius: 6,
                        backgroundColor: showTracking[order.order_id] ? primaryColor : "#fff",
                        color: showTracking[order.order_id] ? "#fff" : primaryColor,
                        cursor: "pointer",
                      }}
                      onClick={() => setShowTracking(prev => ({ ...prev, [order.order_id]: !prev[order.order_id] }))}
                    >
                      Track Order
                    </button>
                    <button
                      style={{
                        flex: 1,
                        padding: "6px 0",
                        border: `1px solid ${primaryColor}`,
                        borderRadius: 6,
                        backgroundColor: "#fff",
                        color: primaryColor,
                        cursor: isDelivered ? "not-allowed" : "pointer",
                        opacity: isDelivered ? 0.5 : 1,
                      }}
                      disabled={isDelivered}
                    >
                      Cancel
                    </button>
                    <button
                      style={{
                        flex: 1,
                        padding: "6px 0",
                        border: `1px solid ${primaryColor}`,
                        borderRadius: 6,
                        backgroundColor: "#fff",
                        color: primaryColor,
                        cursor: isDelivered ? "pointer" : "not-allowed",
                        opacity: isDelivered ? 1 : 0.5,
                      }}
                      disabled={!isDelivered}
                    >
                      Return
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Divider */}
           
   {/* Horizontal Order Tracking */}
{showTracking[order.order_id] && (
  <div style={{ marginTop: 0 }}>
    <h4 style={{ marginBottom: 11 }}>Order Tracking</h4>

    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        paddingTop: 16,
         paddingBottom: 16,
        backgroundColor: "#fff",
        borderRadius: 10,
        border: "1px solid #eee",
        width: "100%",
        overflowX: "auto",
      }}
    >
      {order.order_details?.map((item: OrderItem) =>
        item.order_detail_tracks?.map((track: OrderDetailTrack, index: number, arr) => (
          <div
            key={track.order_detail_track_id}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: 1,
              position: "relative",
              minWidth: 150,
            }}
          >
            {/* Dot with tick */}
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: track.is_active ? primaryColor : "#ccc",
                color: "#fff",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: 14,
                zIndex: 1,
              }}
            >
              {track.is_active ? "✓" : ""}
            </div>

            {/* Horizontal dotted line between dots */}
            {index < arr.length - 1 && (
              <div
                style={{
                  position: "absolute",
                  top: 12, // vertically center
                  left: "50%",
                  width: "100%",
                  height: 2,
                  borderBottom: "1px dotted #ccc",
                  zIndex: 0,
                }}
              />
            )}

            <span style={{ fontWeight: track.is_active ? 600 : 400, textAlign: "center", marginTop: 4 }}>
              {track.status}
            </span>
            {track.status_updated_date && (
              <span style={{ fontSize: 12, color: "#555", textAlign: "center" }}>
                {formatDate(track.status_updated_date)}
              </span>
            )}
          </div>
        ))
      )}
    </div>
  </div>
)}




    
          </div>
        );
      })}
    </div>
  );
};

// ========================== ADDRESS LIST ==========================
const AddressList: React.FC<{ addresses: Address[]; loading: boolean; fetchAddresses: () => void }> = ({ addresses, loading, fetchAddresses }) => {
  const [addAddress, setAddAddress] = useState(false);
  const [editAddress, setEditAddress] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<Address | undefined>(undefined);
  const [deletingAddress, setDeletingAddress] = useState(false);

  const userId = localStorage.getItem("userId");

  const handleDelete = async (id?: string | number) => {
    if (!userId || !id || deletingAddress) return;
    setDeletingAddress(true);
    const success = await removeAddress(userId, id);
    setDeletingAddress(false);
    if (success) fetchAddresses();
    else alert("Failed to delete address");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={() => setAddAddress(true)}
          style={addBtnStyle}
        >
          Add New Address
        </button>
      </div>

      {(addAddress || editAddress) && (
        <AddAddress
          open={addAddress || editAddress}
          address={editAddress ? selectedAddress : undefined}
          onSave={() => { fetchAddresses(); setAddAddress(false); setEditAddress(false); }}
          onClose={() => { setAddAddress(false); setEditAddress(false); }}
        />
      )}

      {loading ? <div>Loading addresses...</div> : addresses.length === 0 ? <div>No addresses found.</div> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {addresses.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0)).map(addr => (
            <div key={addr.address_id} style={{
              display: "flex", flexDirection: "column", padding: 16, borderRadius: 10,
              border: `1px solid ${addr.is_default ? "#ff4f87" : "#ddd"}`,
              backgroundColor: addr.is_default ? "#ffe6ef" : "#fff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 16 }}>
                    {addr.first_name} {addr.last_name} {addr.is_default && <span style={{ fontSize: 12, fontWeight: 500, color: "#ff4f87" }}>(Primary)</span>}
                  </span>
                  <span style={{ fontSize: 14, color: "#555" }}>
                    {addr.address1}{addr.address2 ? `, ${addr.address2}` : ""}{addr.land_mark ? `, ${addr.land_mark}` : ""}, {addr.city}, {addr.state} - {addr.zip_code}, {addr.country}
                  </span>
                  <span style={{ fontSize: 14, color: "#666" }}>
                    📞 {addr.phone_number}{addr.email && <> | 📧 {addr.email}</>}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { setSelectedAddress(addr); setEditAddress(true); }} style={editBtnStyle}>Edit</button>
                  <button onClick={() => handleDelete(addr.address_id)} style={deleteBtnStyle}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========================== COMMON STYLES ==========================
const inputStyle: React.CSSProperties = { padding: "10px 12px", border: "1px solid #ccc", borderRadius: 6, fontSize: 16 };
const saveBtnStyle: React.CSSProperties = { alignSelf: "flex-start", marginTop: 15, backgroundColor: primaryColor, color: "#fff", border: "none", padding: "10px 20px", borderRadius: 6, cursor: "pointer", fontWeight: 600 };
const addBtnStyle: React.CSSProperties = { backgroundColor: primaryColor, display: "flex", marginBottom: "5px", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 500, fontSize: 15 };
const editBtnStyle: React.CSSProperties = { backgroundColor: primaryColor, color: "#fff", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 15, cursor: "pointer" };
const deleteBtnStyle: React.CSSProperties = { backgroundColor: "#f5f5f5", padding: "4px 8px", borderRadius: 8, fontSize: 17, cursor: "pointer", color: "grey" };

// ========================== WEB STYLES ==========================
const webStyles: { [key: string]: React.CSSProperties } = {
  page: { fontFamily: "Arimo, sans-serif" },
  contentRow: { display: "flex", gap: 20, padding: 20, margin: "0 12%" },
  sidebar: { flex: "0 0 250px", border: "1px solid #ddd", borderRadius: 10, padding: 15, backgroundColor: "#fff", display: "flex", flexDirection: "column", gap: 10 },
  profileHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 20 },
  avatar: { width: 50, height: 50, borderRadius: "50%", backgroundColor: primaryColor, color: "#fff", fontSize: 22, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center" },
  name: { fontSize: 16, fontWeight: "bold", marginBottom: 5 },
  phone: { fontSize: 14, color: "#666", marginTop: 0 },
  menuItem: { padding: "10px 15px", borderRadius: 8, cursor: "pointer", fontSize: 16 },
  section: { flex: 1, border: "1px solid #ddd", borderRadius: 10, padding: 20, backgroundColor: "#fff" },
  statusContainer: { margin: "30px auto", maxWidth: 600, padding: 30, borderRadius: 12, backgroundColor: "#ffe6ef", display: "flex", justifyContent: "center", alignItems: "center" },
  loaderBox: { display: "flex", flexDirection: "column", alignItems: "center", gap: 12 },
  spinner: { width: 32, height: 32, border: "3px solid #fff", borderTop: "3px solid #ff4f87", borderRadius: "50%", animation: "spin 1s linear infinite" },
  statusText: { fontSize: 16, fontWeight: 500, color: "#333" },
  statusEmoji: { fontSize: 28 },
};

// ========================== MOBILE STYLES ==========================
const mobileStyles: { [key: string]: React.CSSProperties } = {
  page: { fontFamily: "Arimo, sans-serif", padding: 10 },
  header: { backgroundColor: "#693B2F", color: "#fff", fontSize: 18, fontWeight: 500, textAlign: "center", padding: 20, borderRadius: 8, marginBottom: 10 },
  profileHeader: { display: "flex", alignItems: "center", justifyContent: "center", alignContent: "center", gap: 10, marginBottom: 15 },
  avatar: { width: 50, height: 50, borderRadius: "50%", backgroundColor: primaryColor, color: "#fff", fontSize: 22, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center" },
  name: { fontSize: 16, fontWeight: "bold", marginBottom: 2 },
  phone: { fontSize: 14, color: "#666" },
  tabsContainer: { display: "flex", justifyContent: "space-between", marginBottom: 10, borderRadius: 8, overflow: "hidden", border: "1px solid #ddd" },
  tab: {
    flex: 1, padding: 12,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",   // centers horizontally
    justifyContent: "center", // centers vertically
    textAlign: "center", cursor: "pointer", fontSize: 15
  },
  tabContent: { padding: 10 },
};
